import React from 'react'

import Box from '@mui/material/Box'
import useDisplayDistance from '@/hooks/useDisplayDistance'
import LinkButton from '@/components/EMT/LinkButton'
import { Routes } from '@/assets/configs'
// import useCurrentUser from '@/hooks/useCurrentUser'

// TYPES
import { EmtChip, EmtIcon, EmtSeparator, EmtTypography, useMobile } from 'emt-components-v2'
import { TrustBadges } from './Badges'
import PlainBadges from './PlainBadges'
import Stats from './Stats'
import { useTranslation } from 'next-i18next'
import { Badge, PlainWidthBadgeType } from '@/data/PetSitter/PetSitterProfile'

type Props = React.PropsWithChildren<{
  distance?: number
  city?: string
  zipCode?: string
  firstName: string
  reviewsToCount: number
  averageReviewToRating: number
  publicProfile?: boolean
  petsitter?: boolean
  modifyProfile?: boolean
  badges?: Badge[]
  data?: any
  plainBadges?: PlainWidthBadgeType[]
  variant?: 'reservation'
  petsitterTitle?: string
  title?: string
  description?: string
  petsitterId?: string
  statsData?: {
    recurringCustomersCount: number
    completedServicesCount: number
    averageResponseTime: string
  }
  isReviewsLoading?: boolean
  onScrollToSection?: (sectionId: string) => void
}>

export const UserDetails: React.FC<Props> = ({
  distance,
  city,
  zipCode,
  firstName,
  reviewsToCount,
  averageReviewToRating,
  petsitter,
  modifyProfile,
  badges,
  data,
  plainBadges,
  variant,
  petsitterTitle,
  title,
  description,
  petsitterId,
  statsData,
  isReviewsLoading,
  onScrollToSection,
}) => {
  const { t: tp } = useTranslation('petsitter')
  const { t: tc } = useTranslation('common')
  const [isMobile] = useMobile()
  const finalDistance = useDisplayDistance()(distance)
  // const { currentUser } = useCurrentUser()

  const reservationVariant = variant === 'reservation'

  const getIconFontSize = (icon: string) => {
    switch (icon) {
      case 'emt-knowledge':
      case 'emt-psychology':
        return '14px!important'
      case 'emt-id':
        return '14px!important'
      case 'emt-tick':
        return '10px!important'
      default:
        return '10px!important'
    }
  }

  return (
    <Box>
      {/* Title & stars */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EmtTypography
            variant={isMobile ? 'h4' : 'h2'}
            fontWeight={500}
            sx={{ maxWidth: isMobile ? 220 : 650, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            <b>{firstName}</b>
          </EmtTypography>
        </Box>

        {/* Distance */}

        {city && (
          <Box
            sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center' }}
            onClick={() => {
              onScrollToSection?.('map-section')
            }}
            title={city || finalDistance || ''}
          >
            <EmtIcon className="emt-pin-1" sx={{ color: 'greyGradient.light', fontWeight: 600 }} fontSize="small" />
            <EmtTypography variant="body1" lineHeight={1}>
              <b>{city}</b>
              <span style={{ fontSize: '14px' }}>{zipCode ? ` (${zipCode})` : ''}</span>
            </EmtTypography>
          </Box>
        )}
        {/* End Distance */}
      </Box>
      {/* End Title and stars */}

      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '12px' }}>
        {modifyProfile && data && (
          <Box sx={{ marginBottom: '18px' }}>
            <EmtTypography variant="body2" fontSize="18px" fontWeight={700} sx={{ marginBottom: '15px' }}>
              {tp('profile.userDetails.trustBadgesTitle')}
            </EmtTypography>
            <TrustBadges data={data} />
          </Box>
        )}

        {!modifyProfile && badges && badges.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {badges.map((badge, index) => {
              const isKnowledge = badge.label.icon === 'emt-knowledge'
              const isPsychology = badge.label.icon === 'emt-psychology'

              const targetId = isKnowledge ? 'knowledge-section' : isPsychology ? 'psychology-section' : null

              const isClickable = Boolean(targetId)

              return (
                <EmtChip
                  key={index}
                  icon={
                    <EmtIcon
                      className={badge.label.icon}
                      sx={{
                        fontWeight: 100,
                        fontSize: getIconFontSize(badge.label.icon),
                      }}
                    />
                  }
                  color={badge.color}
                  label={badge.label.label}
                  size="medium"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    '& .MuiChip-label': { paddingLeft: '8px' },
                    cursor: isClickable ? 'pointer' : 'default',
                  }}
                  onClick={isClickable && targetId ? () => onScrollToSection?.(targetId) : undefined}
                />
              )
            })}
          </Box>
        )}

        {/* Pet sitter title */}
        {petsitterTitle && (
          <EmtTypography variant="h5" fontWeight={500}>
            <b>{petsitterTitle}</b>
          </EmtTypography>
        )}
        {/* END Pet sitter title */}

        {!modifyProfile && !reservationVariant && (
          <>
            {!isMobile && <EmtSeparator />}
            <Stats
              statsData={statsData}
              averageReviewToRating={averageReviewToRating}
              reviewsToCount={reviewsToCount}
              onScrollToSection={onScrollToSection}
              isReviewsLoading={isReviewsLoading}
              showZeroStats={false}
            />
            {!isMobile && <EmtSeparator />}
          </>
        )}

        {/* @ts-ignore */}
        {!modifyProfile && !reservationVariant && plainBadges && plainBadges.length > 0 && (
          <PlainBadges plainBadges={plainBadges} />
        )}

        {/* Description */}
        {reservationVariant && (
          <>
            <EmtTypography variant="body2" fontSize="18px" fontWeight={700}>
              {title}
            </EmtTypography>
            <EmtTypography variant="body2" color="greyGradient.light" fontSize="16px">
              {description}
            </EmtTypography>
            <Box sx={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
              {petsitterId && (
                <Box>
                  <LinkButton
                    variant="outlined"
                    color="purple"
                    href={Routes.ProfilePetSitter(petsitterId)}
                    sx={{ backgroundColor: 'transparent!important', justifySelf: 'flex-start!important' }}
                  >
                    {tc('seeProfile')}
                  </LinkButton>
                </Box>
              )}
              {petsitterId && (
                <Box>
                  <LinkButton
                    variant="outlined"
                    color="purple"
                    href={`${Routes.ProfilePetSitter(petsitterId)}#reviews`}
                    sx={{ backgroundColor: 'transparent!important', justifySelf: 'flex-start!important' }}
                  >
                    {tc('putReview')}
                  </LinkButton>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
